"use client";

import { useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import superjson from "superjson";
import { trpc, getBaseUrl } from "@/lib/trpc";
import { supabase } from "@/lib/supabase";

/**
 * Create Query Client
 *
 * Creates a new QueryClient with default options.
 */
function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Don't refetch on window focus in development
        refetchOnWindowFocus: process.env.NODE_ENV === "production",
        // Stale time of 1 minute
        staleTime: 60 * 1000,
      },
    },
  });
}

// Browser singleton
let browserQueryClient: QueryClient | undefined;

function getQueryClient(): QueryClient {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return makeQueryClient();
  }

  // Browser: make a new query client if we don't already have one
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }

  return browserQueryClient;
}

interface TRPCProviderProps {
  children: ReactNode;
}

/**
 * TRPC Provider
 *
 * Wraps the app with tRPC and React Query providers.
 * This enables tRPC hooks throughout the application.
 */
export function TRPCProvider({ children }: TRPCProviderProps) {
  const queryClient = getQueryClient();

  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: `${getBaseUrl()}/api/trpc`,
          transformer: superjson,
          async headers() {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.access_token) {
              return { Authorization: `Bearer ${session.access_token}` };
            }
            return {};
          },
        }),
      ],
    })
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
